import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Cmd+K search modal with debounced fetch, keyboard navigation, and recent searches
// Registered as "docs--search"
export default class extends Controller {
  static targets = ["modal", "input", "results", "empty", "loading", "list"]
  static values = { suggestionsUrl: String }

  static RECENT_KEY = "docs_recent_searches"
  static MAX_RECENT = 5

  connect() {
    this._boundKeydown = this._handleGlobalKeydown.bind(this)
    document.addEventListener("keydown", this._boundKeydown)
    this._debounceTimer = null
    this._selectedIndex = -1
    this._recentSearches = this._loadRecentSearches()
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundKeydown)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    // Reset any leftover body scroll-lock. Without this, clicking a search
    // result while the modal is open triggers a Turbo Drive navigation that
    // unmounts the modal *without* firing close() — so body stays
    // overflow:hidden and the next page is unscrollable.
    if (this._bodyOverflowLocked) {
      document.body.style.overflow = ""
      this._bodyOverflowLocked = false
    }
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._bodyOverflowLocked = true
    requestAnimationFrame(() => {
      this.inputTarget.focus()
      // Show recent searches when opening with empty input
      if (!this.inputTarget.value.trim()) {
        this._showRecentSearches()
      }
    })
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._bodyOverflowLocked = false
    this.inputTarget.value = ""
    this._resetResults()
  }

  onInput() {
    const query = this.inputTarget.value.trim()

    if (this._debounceTimer) clearTimeout(this._debounceTimer)

    if (query.length < 2) {
      if (!query) {
        this._showRecentSearches()
      } else {
        this._resetResults()
      }
      return
    }

    this._debounceTimer = setTimeout(() => this._fetchSuggestions(query), 300)
  }

  onKeydown(event) {
    const items = this.listTarget.querySelectorAll("[role='option']")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._selectedIndex = Math.min(
          this._selectedIndex + 1,
          items.length - 1,
        )
        this._highlightItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
        this._highlightItem(items)
        break
      case "Enter":
        event.preventDefault()
        if (this._selectedIndex >= 0 && items[this._selectedIndex]) {
          const link = items[this._selectedIndex].querySelector("a")
          if (link) {
            this._saveRecentSearch(this.inputTarget.value.trim())
            window.location.href = link.href
          }
        } else if (this.inputTarget.value.trim().length >= 2) {
          this._saveRecentSearch(this.inputTarget.value.trim())
          const searchUrl = this.suggestionsUrlValue.replace("/suggestions", "")
          window.location.href = `${searchUrl}?q=${encodeURIComponent(this.inputTarget.value.trim())}`
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  selectRecent(event) {
    const query = event.currentTarget.dataset.query
    if (!query) return
    this.inputTarget.value = query
    this._fetchSuggestions(query)
  }

  // Private

  _handleGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      if (this.modalTarget.classList.contains("hidden")) {
        this.open()
      } else {
        this.close()
      }
    }
  }

  async _fetchSuggestions(query) {
    this._showLoading()

    try {
      const url = `${this.suggestionsUrlValue}?q=${encodeURIComponent(query)}`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) return

      const payload = await response.json
      // Backwards-compatible: legacy endpoint returned a bare array.
      const suggestions = Array.isArray(payload)
        ? payload
        : payload.suggestions || []
      const semantic = Array.isArray(payload) ? [] : payload.semantic || []
      this._renderSuggestions(suggestions, semantic, query)
    } catch {
      this._resetResults()
    }
  }

  _renderSuggestions(suggestions, semantic, query) {
    this._selectedIndex = -1
    this.emptyTarget.classList.add("hidden")
    this.loadingTarget.classList.add("hidden")

    if (suggestions.length === 0 && semantic.length === 0) {
      this.listTarget.classList.add("hidden")
      this.emptyTarget.textContent = "No results found"
      this.emptyTarget.classList.remove("hidden")
      return
    }

    // Rebuild list from scratch via safe DOM APIs (no innerHTML of untrusted content).
    while (this.listTarget.firstChild) {
      this.listTarget.removeChild(this.listTarget.firstChild)
    }

    this._appendSection("Matches", suggestions, query, { withScore: false })
    this._appendSection("Ask the docs", semantic, query, { withScore: true })

    this.listTarget.classList.remove("hidden")
  }

  _appendSection(label, items, query, { withScore }) {
    if (!items.length) return

    const header = document.createElement("li")
    header.className =
      "px-4 py-2 text-[10px] uppercase tracking-wider text-foreground/40 font-medium select-none"
    header.textContent = label
    this.listTarget.appendChild(header)

    items.forEach((item) => {
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.setAttribute("aria-selected", "false")

      const a = document.createElement("a")
      a.href = item.url
      a.className =
        "flex items-center justify-between px-4 py-2.5 hover:bg-muted transition-colors"

      const leftWrap = document.createElement("div")
      leftWrap.className = "min-w-0"
      const titleEl = document.createElement("div")
      titleEl.className = "text-sm font-medium text-foreground truncate"
      this._fillHighlighted(titleEl, item.title || "", query)
      leftWrap.appendChild(titleEl)

      const rightWrap = document.createElement("div")
      rightWrap.className = "flex items-center shrink-0 ml-3"

      const catBadge = document.createElement("span")
      catBadge.className =
        "rounded-full bg-muted px-2 py-0.5 text-xs text-foreground/60"
      catBadge.textContent = item.category || ""
      rightWrap.appendChild(catBadge)

      if (withScore && item.similarity != null) {
        const score = document.createElement("span")
        score.className =
          "ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
        score.textContent = `${Math.round(item.similarity)}%`
        rightWrap.appendChild(score)
      }

      a.appendChild(leftWrap)
      a.appendChild(rightWrap)
      a.addEventListener("click", () => this._saveRecentSearch(query))
      li.appendChild(a)
      this.listTarget.appendChild(li)
    })
  }

  // Append the text, wrapping query matches in <mark> tags. Uses textContent
  // for everything so no untrusted HTML is ever inserted.
  _fillHighlighted(el, text, query) {
    if (!query) {
      el.textContent = text
      return
    }
    const words = query
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    if (!words.length) {
      el.textContent = text
      return
    }
    const pattern = new RegExp(`(${words.join("|")})`, "gi")
    const parts = text.split(pattern)
    const lowerWords = new Set(words.map((w) => w.toLowerCase()))
    parts.forEach((part) => {
      if (!part) return
      if (lowerWords.has(part.toLowerCase())) {
        const mark = document.createElement("mark")
        mark.className = "bg-primary/20 text-foreground rounded-sm px-0.5"
        mark.textContent = part
        el.appendChild(mark)
      } else {
        el.appendChild(document.createTextNode(part))
      }
    })
  }

  _showRecentSearches() {
    if (this._recentSearches.length === 0) return

    this._selectedIndex = -1
    this.emptyTarget.classList.add("hidden")
    this.loadingTarget.classList.add("hidden")

    const clockIcon = `<svg class="h-4 w-4 text-foreground/30 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`

    this.listTarget.innerHTML = `
      <li class="px-4 py-2 text-[10px] uppercase tracking-wider text-foreground/40 font-medium select-none">Recent</li>
      ${this._recentSearches
        .map(
          (q) => `
        <li role="option" aria-selected="false">
          <button type="button"
            class="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left"
            data-action="click->docs--search#selectRecent"
            data-query="${this._escapeHtml(q)}"
          >
            ${clockIcon}
            <span class="text-sm text-foreground/70">${this._escapeHtml(q)}</span>
          </button>
        </li>
      `,
        )
        .join("")}
    `
    this.listTarget.classList.remove("hidden")
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      const link = item.querySelector("a, button")
      if (i === this._selectedIndex) {
        item.setAttribute("aria-selected", "true")
        link?.classList.add("bg-muted")
      } else {
        item.setAttribute("aria-selected", "false")
        link?.classList.remove("bg-muted")
      }
    })
  }

  _showLoading() {
    this.emptyTarget.classList.add("hidden")
    this.listTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("hidden")
  }

  _resetResults() {
    this._selectedIndex = -1
    this.loadingTarget.classList.add("hidden")
    this.listTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
    this.emptyTarget.textContent = "Type to search documentation..."
    this.emptyTarget.classList.remove("hidden")
  }

  _saveRecentSearch(query) {
    const cleaned = query.trim()
    if (!cleaned || cleaned.length < 2) return
    const updated = [
      cleaned,
      ...this._recentSearches.filter((q) => q !== cleaned),
    ].slice(0, this.constructor.MAX_RECENT)
    try {
      localStorage.setItem(this.constructor.RECENT_KEY, JSON.stringify(updated))
      this._recentSearches = updated
    } catch {
      // localStorage unavailable
    }
  }

  _loadRecentSearches() {
    try {
      const stored = localStorage.getItem(this.constructor.RECENT_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
